/**
 * Shared NavBar component — used across all pages.
 * Renders the GottaSecond logo and optional right-side actions.
 * Includes full profile dropdown with toggles, notifications, password, logout.
 */
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  User, LogOut, Settings, MessageSquare, ChevronDown, 
  Bell, Lock, CreditCard, Power, Camera, Briefcase, Eye, HelpCircle
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_20a21bfd-b8fc-46d5-80c1-3ef1449007db/artifacts/gnn6v1fg_Gotta%20Second-white.png";

export function NavBar({ children }) {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 gs-nav" data-testid="navbar">
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 py-3 flex items-center justify-between">
        <Link to="/" className="shrink-0" data-testid="nav-logo">
          <img
            src={LOGO_URL}
            alt="GottaSecond"
            className="h-8 md:h-10 w-auto"
          />
        </Link>
        <div className="flex items-center gap-3">{children}</div>
      </div>
    </nav>
  );
}

function ToggleSwitch({ checked, onChange, disabled, testId }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(); }}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      } ${checked ? "bg-coral" : "bg-white/20"}`}
      data-testid={testId}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
          checked ? "translate-x-5" : "translate-x-1"
        }`}
      />
    </button>
  );
}

/**
 * NavActions - Standard navigation actions for authenticated pages
 * Includes messages link with unread indicator and FULL profile dropdown
 */
export function NavActions({ showMessages = true }) {
  const navigate = useNavigate();
  const { user, token, logout, updateUser } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const dropdownRef = useRef(null);

  const roles = user?.roles || [];
  const isAssistantRole = roles.includes("assistant");
  const isStudioRole = roles.includes("studio");
  
  // Separate active states for each role
  const assistantActive = user?.assistant_profile?.find_work_active !== false;
  const studioActive = user?.studio_profile?.hiring_active !== false;

  // Fetch unread message count
  useEffect(() => {
    if (token && showMessages) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [token, showMessages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      } else if (response.status === 401) {
        // Token expired — stop polling silently (user will be prompted to login on next action)
        setUnreadCount(0);
      }
    } catch (error) {
      // Network error — silently ignore, will retry on next poll
    }
  };

  const saveUpdate = async (updateData) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/users/profile`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        updateUser(updatedUser);
        toast.success("Settings updated");
      } else if (response.status === 429) {
        toast.error("Too many changes at once — wait a minute and try again");
      } else {
        toast.error("Failed to update");
      }
    } catch {
      toast.error("Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleAssistantActive = () => {
    const newValue = !assistantActive;
    saveUpdate({ 
      assistant_profile: {
        ...user?.assistant_profile,
        find_work_active: newValue
      }
    });
  };

  const toggleStudioActive = () => {
    const newValue = !studioActive;
    saveUpdate({ 
      studio_profile: {
        ...user?.studio_profile,
        hiring_active: newValue
      }
    });
  };

  const enableRole = async (role) => {
    const newRoles = [...roles];
    if (!newRoles.includes(role)) {
      newRoles.push(role);
    }
    await saveUpdate({ roles: newRoles });
  };

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
    navigate("/");
  };

  if (!user) return null;

  return (
    <div className="flex items-center gap-3">
      {/* Help Link */}
      <Link to="/help" data-testid="nav-help">
        <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10">
          <HelpCircle className="w-5 h-5" />
        </Button>
      </Link>

      {/* Messages Link */}
      {showMessages && (
        <Link to="/messages" className="relative" data-testid="nav-messages">
          <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10">
            <MessageSquare className="w-5 h-5" />
          </Button>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-coral text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Link>
      )}

      {/* Profile Dropdown - FULL VERSION */}
      <div className="relative" ref={dropdownRef}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="text-white/60 hover:text-white hover:bg-white/10 flex items-center gap-1"
          data-testid="nav-profile-dropdown"
        >
          {user.profile_image ? (
            <img src={user.profile_image} alt="" className="w-6 h-6 rounded-full object-cover" />
          ) : (
            <User className="w-5 h-5" />
          )}
          <ChevronDown className={`w-3 h-3 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
        </Button>

        {dropdownOpen && (
          <div className="absolute right-0 top-full mt-2 w-72 bg-[#141414] border border-white/10 rounded-lg shadow-xl overflow-hidden z-50">
            {/* User Info */}
            <div className="px-4 py-3 border-b border-white/10">
              <p className="text-sm font-medium text-white truncate">{user.name}</p>
              <p className="text-xs text-white/50 truncate">{user.email}</p>
            </div>

            {/* View Profile Link */}
            <div className="px-2 py-2 border-b border-white/10">
              <Link
                to={`/profile/${user.user_id}`}
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
              >
                <Eye className="w-4 h-4" />
                View Profile
              </Link>
            </div>
            
            {/* Active Status Toggles */}
            <div className="px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wider mb-3">
                <Power className="w-3 h-3" />
                Active Status
              </div>
              
              <div className="space-y-3">
                {/* For-Hire Profile Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Camera className="w-4 h-4 text-coral" />
                    <span className="text-sm text-white/80">For-Hire Profile</span>
                  </div>
                  {isAssistantRole ? (
                    <ToggleSwitch
                      checked={assistantActive}
                      onChange={toggleAssistantActive}
                      disabled={saving}
                      testId="toggle-assistant-active"
                    />
                  ) : (
                    <button
                      onClick={() => enableRole("assistant")}
                      className="text-xs text-coral hover:text-coral/80"
                      disabled={saving}
                    >
                      Enable
                    </button>
                  )}
                </div>

                {/* Studio Profile Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[#3498DB]" />
                    <span className="text-sm text-white/80">Studio Profile</span>
                  </div>
                  {isStudioRole ? (
                    <ToggleSwitch
                      checked={studioActive}
                      onChange={toggleStudioActive}
                      disabled={saving}
                      testId="toggle-studio-active"
                    />
                  ) : (
                    <button
                      onClick={() => enableRole("studio")}
                      className="text-xs text-[#3498DB] hover:text-[#3498DB]/80"
                      disabled={saving}
                    >
                      Enable
                    </button>
                  )}
                </div>
              </div>
              <p className="text-white/30 text-xs mt-2">Hidden from searches when off</p>
            </div>

            {/* Navigation Links */}
            <div className="px-2 py-2 space-y-0.5">
              {isAssistantRole && (
                <Link
                  to="/profile/edit"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
                >
                  <Camera className="w-4 h-4" />
                  Edit For-Hire Profile
                </Link>
              )}
              {isStudioRole && (
                <Link
                  to="/profile/edit/studio"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
                >
                  <Briefcase className="w-4 h-4" />
                  Edit Studio Profile
                </Link>
              )}
              <Link
                to="/notifications/settings"
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
              >
                <Bell className="w-4 h-4" />
                Notification Settings
              </Link>
              <Link
                to="/password-reset"
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
              >
                <Lock className="w-4 h-4" />
                Password & Security
              </Link>
              <Link
                to="/pricing"
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
              >
                <CreditCard className="w-4 h-4" />
                Subscription & Billing
              </Link>
            </div>

            {/* Logout */}
            <div className="px-2 py-2 border-t border-white/10">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
                data-testid="nav-logout"
              >
                <LogOut className="w-4 h-4" />
                Log Out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
